import { IAccount, ISession } from '../types';
import urls from '../urls';
import retryIfTimeout from '../util/retryIfTimeout';
import { SESSION_STORAGE_KEY } from '../constants';

function getSessionFromLocalStorage(): ISession {
  const session = JSON.parse(localStorage.getItem(SESSION_STORAGE_KEY)!) as
    | ISession
    | undefined;

  if (!session) {
    throw new Error('Cannot find the current user session.');
  }

  return session;
}

export async function getAccounts(
  accessToken: string
): Promise<{ accounts: IAccount[] }> {
  return retryIfTimeout(() => {
    return fetch(urls.accounts, {
      method: 'GET',
      headers: { Authorization: `Bearer ${accessToken}` },
    });
  });
}

export async function getPolicyForAccount(
  accessToken: string,
  accountId: string
) {
  return retryIfTimeout(() => {
    let url = urls.accountById.replace('${ACCOUNT_ID}', accountId);

    return fetch(url + '?include=policy', {
      method: 'GET',
      headers: { Authorization: `Bearer ${accessToken}` },
    });
  });
}

export async function createAccount(
  id: string,
  name: string,
  email?: string
): Promise<Response> {
  const session = getSessionFromLocalStorage();
  const accountType = 'FREE';

  return fetch(urls.accounts, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.accessToken}`,
    },
    body: JSON.stringify({
      owner: email || session.email,
      name,
      id,
      type: accountType,
    }),
  });
}

export async function patchAccount(
  id: string,
  updates: Partial<Pick<IAccount, 'name' | 'owner' | 'deletionDate'>>
): Promise<IAccount> {
  const session = getSessionFromLocalStorage();
  const response = await fetch(`${urls.accounts}/${id}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.accessToken}`,
    },
    body: JSON.stringify({
      ...updates,
    }),
  });

  if (response.status > 400) {
    throw new Error(response.statusText);
  } else {
    return (await response.json()) as IAccount;
  }
}

export async function updateAccountLogo(
  id: string,
  logoUrl: string
): Promise<Response> {
  const session = getSessionFromLocalStorage();
  const response = await fetch(`${urls.accounts}/${id}/set-logo-url`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.accessToken}`,
    },
    body: JSON.stringify({
      logoUrl,
    }),
  });

  if (response.status >= 400) {
    throw new Error(response.statusText);
  } else {
    return await response.json();
  }
}
